import { useFeatureFlag } from "@jsq/flags-react";
import { useGetPaginatedPositionsInAccountWithFiltersQuery } from "#graphql/supergraphHooks";
import {
  type CountryFilter,
  type GetPaginatedPositionsInAccountWithFiltersQuery,
} from "#graphql/supergraphTypes";
import { PaymentMethod } from "../../../../common/enums";
import { WorkflowMode } from "../../common/enums";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import {
  FETCH_MORE_POSITIONS_PER_PAGE,
  INITIAL_POSITIONS_PER_PAGE,
} from "../PositionList/constants";

const COUNTRY_FILTERED_PAYMENT_METHODS = new Set([PaymentMethod.Wire, PaymentMethod.Check]);

export const usePositionsCountryFilter = (): CountryFilter | undefined => {
  const internationalPaymentRails = useFeatureFlag("internationalPaymentRails", false);
  const { workflowMode, selectedPaymentMethod, paymentPrefId, selectedBankCountry } =
    useConsolidatedPaymentPrefContext();
  if (!internationalPaymentRails) {
    return undefined;
  }
  if (!selectedPaymentMethod || !COUNTRY_FILTERED_PAYMENT_METHODS.has(selectedPaymentMethod)) {
    return undefined;
  }
  if (workflowMode === WorkflowMode.Edit) {
    return selectedBankCountry?.id ? { id: selectedBankCountry.id } : undefined;
  }
  if (paymentPrefId) {
    return { paymentPrefId };
  }
  if (selectedBankCountry?.id) {
    return { id: selectedBankCountry.id };
  }
  return undefined;
};

export const usePaginatedPositionsHook = ({
  accountId,
  paymentPrefId,
  supportedPaymentMethod,
  unsupportedPaymentMethod,
  skip,
  hasPaymentPref,
  country,
}: {
  accountId: string;
  skip: boolean;
  paymentPrefId?: string;
  supportedPaymentMethod?: PaymentMethod;
  unsupportedPaymentMethod?: PaymentMethod;
  hasPaymentPref?: boolean;
  country?: CountryFilter;
}) => {
  const { data, loading, fetchMore, error } = useGetPaginatedPositionsInAccountWithFiltersQuery(
    {
      variables: {
        accountId,
        first: INITIAL_POSITIONS_PER_PAGE,
        filters: {
          paymentPrefId,
          supportedPaymentMethod,
          unsupportedPaymentMethod,
          ...(hasPaymentPref !== undefined && { hasPaymentPref }),
          ...(country && { country }),
        },
      },
      skip,
      fetchPolicy: "network-only",
    },
  );

  const unifiedUpdateQuery = (
    previousResult: GetPaginatedPositionsInAccountWithFiltersQuery,
    {
      fetchMoreResult,
    }: {
      fetchMoreResult: GetPaginatedPositionsInAccountWithFiltersQuery;
    },
  ) => {
    if (!fetchMoreResult?.account?.positions?.edges) {
      return previousResult;
    }

    if (!previousResult.account?.positions?.edges) {
      return fetchMoreResult;
    }

    return {
      account: {
        ...previousResult.account,
        positions: {
          ...previousResult.account.positions,
          edges: [
            ...(previousResult.account.positions?.edges ?? []),
            ...(fetchMoreResult.account.positions?.edges ?? []),
          ],
          pageInfo: fetchMoreResult.account.positions.pageInfo,
        },
      },
    };
  };

  const fetchMoreHandler = async () => {
    if (!data?.account?.positions?.pageInfo.hasNextPage || !accountId) {
      return;
    }
    await fetchMore({
      variables: {
        accountId,
        filters: {
          paymentPrefId,
          supportedPaymentMethod,
          unsupportedPaymentMethod,
          ...(hasPaymentPref !== undefined && { hasPaymentPref }),
          ...(country && { country }),
        },
        first: FETCH_MORE_POSITIONS_PER_PAGE,
        after: data?.account?.positions?.pageInfo.endCursor,
      },
      updateQuery: unifiedUpdateQuery,
    });
  };

  return { data, loading, fetchMoreHandler, error };
};
